/** Dashboard permission keys for enquiry sub-tabs (Manage Permissions page). */
export const SOS_EMERGENCY_PERMISSION = "/SOS_Emergency";
export const CAMPAIGN_CONTACTS_PERMISSION = "/Campaign_Contacts";
export const CHATBOT_LEADS_PERMISSION = "/Chatbot_Leads";

/** Legacy key still accepted for SOS tab/sidebar access. */
export const SOS_EMERGENCY_PERMISSION_LEGACY = "/sos/list";

export const SOS_EMERGENCY_PERMISSIONS = [
  SOS_EMERGENCY_PERMISSION,
  SOS_EMERGENCY_PERMISSION_LEGACY,
];

export const ENQUIRY_SUB_TAB_PERMISSIONS = [
  SOS_EMERGENCY_PERMISSION,
  CAMPAIGN_CONTACTS_PERMISSION,
  CHATBOT_LEADS_PERMISSION,
];

/** Dashboard permission key for Payment Data page. */
export const PAYMENT_DATA_PERMISSION = "/Payment_Data";

/** Activity Log page — prefer singular key from Manage Permissions. */
export const ACTIVITY_LOG_PERMISSION = "/Activity_Log";
/** Legacy / alternate key still accepted. */
export const ACTIVITY_LOG_PERMISSION_LEGACY = "/Activity_Logs";

export const ACTIVITY_LOG_PERMISSIONS = [
  ACTIVITY_LOG_PERMISSION,
  ACTIVITY_LOG_PERMISSION_LEGACY,
];

/** Doctor's Review feature (enquiry / patient doctor-review actions). */
export const DOCTOR_REVIEW_PERMISSION = "/Doctor_Review";
/** Alternate key if backend uses plural variant. */
export const DOCTOR_REVIEW_PERMISSION_ALT = "/Doctors_Review";

export const DOCTOR_REVIEW_PERMISSIONS = [
  DOCTOR_REVIEW_PERMISSION,
  DOCTOR_REVIEW_PERMISSION_ALT,
];

/** Director Messages menu access permission. */
export const DIRECTOR_MESSAGES_PERMISSION = "/Director_Messages";
export const DIRECTOR_MESSAGES_PERMISSION_ALT = "/director-messages";
export const DIRECTOR_MESSAGES_PERMISSION_LOWER = "/director_messages";

export const DIRECTOR_MESSAGES_PERMISSIONS = [
  DIRECTOR_MESSAGES_PERMISSION,
  DIRECTOR_MESSAGES_PERMISSION_ALT,
  DIRECTOR_MESSAGES_PERMISSION_LOWER,
];

/** Send / Broadcast Director Message permission (on behalf of Director). */
export const SEND_DIRECTOR_MESSAGE_PERMISSION = "/Send_Director_Message";
export const SEND_DIRECTOR_MESSAGE_PERMISSION_ALT = "/send-director-message";
export const SEND_DIRECTOR_MESSAGE_PERMISSION_LOWER = "/send_director_message";

export const SEND_DIRECTOR_MESSAGE_PERMISSIONS = [
  SEND_DIRECTOR_MESSAGE_PERMISSION,
  SEND_DIRECTOR_MESSAGE_PERMISSION_ALT,
  SEND_DIRECTOR_MESSAGE_PERMISSION_LOWER,
];

/** Referral module permission. */
export const REFERRAL_PERMISSION = "/Referral";
export const REFERRAL_PERMISSION_ALT = "/referrals";
export const REFERRAL_PERMISSION_MANAGE = "/Manage_Referrals";

export const REFERRAL_PERMISSIONS = [
  REFERRAL_PERMISSION,
  REFERRAL_PERMISSION_ALT,
  REFERRAL_PERMISSION_MANAGE,
];

/** Custom event so Inquiry listing can reopen Navbar's EmergencyAlertModal. */
export const OPEN_SOS_ALERT_EVENT = "omca:open-sos-alert";

const PERMISSION_LABELS = {
  [SOS_EMERGENCY_PERMISSION]: "SOS EMERGENCY",
  [SOS_EMERGENCY_PERMISSION_LEGACY]: "SOS EMERGENCY",
  [CAMPAIGN_CONTACTS_PERMISSION]: "CAMPAIGN CONTACTS",
  [CHATBOT_LEADS_PERMISSION]: "CHATBOT LEADS",
  [PAYMENT_DATA_PERMISSION]: "PAYMENT DATA",
  [ACTIVITY_LOG_PERMISSION]: "ACTIVITY LOG",
  [ACTIVITY_LOG_PERMISSION_LEGACY]: "ACTIVITY LOG",
  [DOCTOR_REVIEW_PERMISSION]: "DOCTOR REVIEW",
  [DOCTOR_REVIEW_PERMISSION_ALT]: "DOCTOR REVIEW",
  [DIRECTOR_MESSAGES_PERMISSION]: "DIRECTOR MESSAGES",
  [DIRECTOR_MESSAGES_PERMISSION_ALT]: "DIRECTOR MESSAGES",
  [DIRECTOR_MESSAGES_PERMISSION_LOWER]: "DIRECTOR MESSAGES",
  [SEND_DIRECTOR_MESSAGE_PERMISSION]: "SEND DIRECTOR MESSAGE",
  [SEND_DIRECTOR_MESSAGE_PERMISSION_ALT]: "SEND DIRECTOR MESSAGE",
  [SEND_DIRECTOR_MESSAGE_PERMISSION_LOWER]: "SEND DIRECTOR MESSAGE",
  [REFERRAL_PERMISSION]: "REFERRAL",
  [REFERRAL_PERMISSION_ALT]: "REFERRAL",
  [REFERRAL_PERMISSION_MANAGE]: "REFERRAL",
};

const ASSIGNABLE_DASHBOARD_PERMISSIONS = [
  ...ENQUIRY_SUB_TAB_PERMISSIONS,
  PAYMENT_DATA_PERMISSION,
  ACTIVITY_LOG_PERMISSION,
  DOCTOR_REVIEW_PERMISSION,
  REFERRAL_PERMISSION,
];

const parsePermissionList = (permissions) =>
  Array.isArray(permissions)
    ? permissions
    : String(permissions || "")
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => (p.startsWith("/") ? p : `/${p}`));

export const hasDashboardPermission = (permissions, permissionKey, role) => {
  const list = parsePermissionList(permissions);
  if (list.includes(permissionKey)) return true;
  return String(role || "").toLowerCase() === "admin";
};

export const hasAnyDashboardPermission = (
  permissions,
  permissionKeys = [],
  role,
) => {
  const list = parsePermissionList(permissions);
  if (permissionKeys.some((key) => list.includes(key))) return true;
  return String(role || "").toLowerCase() === "admin";
};

export const hasSosEmergencyPermission = (permissions, role) => {
  const list = parsePermissionList(permissions);
  if (SOS_EMERGENCY_PERMISSIONS.some((ep) => list.includes(ep))) return true;
  return String(role || "").toLowerCase() === "admin";
};

export const hasCampaignContactsPermission = (permissions, role) =>
  hasDashboardPermission(permissions, CAMPAIGN_CONTACTS_PERMISSION, role);

export const hasChatbotLeadsPermission = (permissions, role) =>
  hasDashboardPermission(permissions, CHATBOT_LEADS_PERMISSION, role);

export const hasActivityLogPermission = (permissions, role) =>
  hasAnyDashboardPermission(permissions, ACTIVITY_LOG_PERMISSIONS, role);

export const hasDoctorReviewPermission = (permissions, role) =>
  hasAnyDashboardPermission(permissions, DOCTOR_REVIEW_PERMISSIONS, role);

export const hasDirectorMessagesPermission = () => true;

export const hasSendDirectorMessagePermission = (permissions, role) => {
  const r = String(role || localStorage.getItem("Role") || "").trim().toLowerCase();
  return r === "admin";
};

export const hasReferralPermission = (permissions, role) =>
  hasAnyDashboardPermission(permissions, REFERRAL_PERMISSIONS, role);

export const hasEnquirySubTabPermission = (permissions, permissionKey, role) => {
  if (permissionKey === SOS_EMERGENCY_PERMISSION) {
    return hasSosEmergencyPermission(permissions, role);
  }
  return hasDashboardPermission(permissions, permissionKey, role);
};

/** Ensure Manage Permissions always shows assignable dashboard permission rows. */
export const ensureEnquirySubTabPermissions = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) return roles;

  return roles.map((role) => {
    const permissions = { ...(role.permissions || {}) };

    ASSIGNABLE_DASHBOARD_PERMISSIONS.forEach((key) => {
      if (permissions[key] == null) {
        permissions[key] = 0;
      }
    });

    if (
      permissions[SOS_EMERGENCY_PERMISSION] == null &&
      permissions[SOS_EMERGENCY_PERMISSION_LEGACY] != null
    ) {
      permissions[SOS_EMERGENCY_PERMISSION] =
        permissions[SOS_EMERGENCY_PERMISSION_LEGACY];
    }

    if (
      permissions[ACTIVITY_LOG_PERMISSION] == null &&
      permissions[ACTIVITY_LOG_PERMISSION_LEGACY] != null
    ) {
      permissions[ACTIVITY_LOG_PERMISSION] =
        permissions[ACTIVITY_LOG_PERMISSION_LEGACY];
    }

    if (
      permissions[DOCTOR_REVIEW_PERMISSION] == null &&
      permissions[DOCTOR_REVIEW_PERMISSION_ALT] != null
    ) {
      permissions[DOCTOR_REVIEW_PERMISSION] =
        permissions[DOCTOR_REVIEW_PERMISSION_ALT];
    }

    return { ...role, permissions };
  });
};

/** @deprecated Use ensureEnquirySubTabPermissions */
export const ensureSosEmergencyPermission = ensureEnquirySubTabPermissions;

export const formatPermissionEndpointLabel = (endpoint) => {
  if (PERMISSION_LABELS[endpoint]) {
    return PERMISSION_LABELS[endpoint];
  }
  return String(endpoint || "")
    .replace(/_/g, " ")
    .replace(/^\//, "")
    .toUpperCase();
};

/** Hide legacy duplicate keys when the preferred dashboard key is present. */
export const getPermissionTableEndpoints = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) return [];

  const endpoints = Object.keys(roles[0]?.permissions || {});
  const hasReadableSos = endpoints.includes(SOS_EMERGENCY_PERMISSION);
  const hasActivityLog = endpoints.includes(ACTIVITY_LOG_PERMISSION);
  const hasDoctorReview = endpoints.includes(DOCTOR_REVIEW_PERMISSION);

  return endpoints.filter((endpoint) => {
    if (hasReadableSos && endpoint === SOS_EMERGENCY_PERMISSION_LEGACY) {
      return false;
    }
    if (hasActivityLog && endpoint === ACTIVITY_LOG_PERMISSION_LEGACY) {
      return false;
    }
    if (hasDoctorReview && endpoint === DOCTOR_REVIEW_PERMISSION_ALT) {
      return false;
    }
    return true;
  });
};

export const openSosAlertFromListing = (sosId, alert = null) => {
  if (!sosId) return;
  window.dispatchEvent(
    new CustomEvent(OPEN_SOS_ALERT_EVENT, {
      detail: { sosId, alert },
    }),
  );
};
